/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { isEmpty, property } from 'lodash';
import { List, Loader } from 'semantic-ui-react';
import { Table, MasterTypeIcon } from '../../../components';
import { FormattedMessage } from 'react-intl';

import MasterDetailsButton from './MasterDetailsButton';
import EditMasterButton from './EditMasterButton';
import DeleteMasterButton from './DeleteMasterButton';
import ServiceGroupName from './ServiceGroupName';

import type { Master } from '../../../type';

export const GET_MASTERS_QUERY = gql`
    query getMasters($salon: ID!, $search: String, $lang: String!) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    masters(search: $search) {
                        pageInfo {
                            hasNextPage
                            endCursor
                        }
                        nodes {
                            id
                            ...master
                        }
                    }
                }
            }
        }
    }

    fragment master on Master {
        firstName
        lastName
        phone
        email
        status
        type
        gender
        serviceGroups {
            id
            name(lang: $lang)
            type
        }
    }
`;

type MasterRowProps = {
    master: Master,
};

const NameCell = styled(Table.Cell)`
    padding: 0px !important;
`;

/**
 * Show this message if list of masters is empty
 */
const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={3} textAlign="center">
            <FormattedMessage
                id="pages.staff.MastersList.noMasters"
                defaultMessage="You don't have masters yet"
            />
        </Table.Cell>
    </Table.Row>
);

/**
 * Represents component with single row of master details
 */
const MasterRow = ({ master }: MasterRowProps) => (
    <Table.Row>
        <NameCell>
            <MasterDetailsButton master={master}>
                <MasterTypeIcon
                    name={`${master.firstName} ${master.lastName}`}
                    type={property(['serviceGroups', '0', 'type'])(master)}
                />
                <span>
                    {master.firstName} {master.lastName}
                </span>
            </MasterDetailsButton>
        </NameCell>
        <Table.Cell>
            {master.serviceGroups.map(serviceGroup => (
                <ServiceGroupName key={serviceGroup.id}>
                    {serviceGroup.name}
                </ServiceGroupName>
            ))}
        </Table.Cell>
        <Table.Cell>
            <List horizontal divided>
                <List.Item>
                    <EditMasterButton master={master} />
                </List.Item>
                <List.Item>
                    <DeleteMasterButton master={master} />
                </List.Item>
            </List>
        </Table.Cell>
    </Table.Row>
);

/**
 * Represents table with list of masters
 */
function MastersList() {
    const salon = useSelector(({ user }) => user.get('salon'));
    const searchQuery = useSelector(({ staff }) => staff.get('searchQuery'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );

    const { data, loading } = useQuery(GET_MASTERS_QUERY, {
        variables: {
            salon,
            search: searchQuery || undefined,
            lang: currentLanguage,
        },
        fetchPolicy: 'cache-and-network',
    });

    if (loading) return <Loader size="big" inline="centered" active />;

    const masters = data?.viewer?.salon?.masters?.nodes ?? [];

    return (
        <Table celled selectable striped fixed>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>
                        <FormattedMessage
                            id="pages.staff.MastersList.name"
                            defaultMessage="Name"
                        />
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        <FormattedMessage
                            id="pages.staff.MastersList.services"
                            defaultMessage="Services"
                        />
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        <FormattedMessage
                            id="pages.staff.MastersList.actions"
                            defaultMessage="Actions"
                        />
                    </Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {isEmpty(masters) ? (
                    <EmptyMessage />
                ) : (
                    masters.map(
                        master =>
                            !!master && (
                                <MasterRow key={master.id} master={master} />
                            ),
                    )
                )}
            </Table.Body>
        </Table>
    );
}

export default MastersList;
